import { useFlags } from 'launchdarkly-react-client-sdk';
import { Navigate } from 'react-router-dom';

import LoadingCard from '@/components/ui/loading-card';
import type { LdFeatureFlags } from '@/config/features';
import type { VerificationProps } from '@/features/verification/components';
import Verification from '@/features/verification/components';
import VerificationLayout from '@/features/verification/layout';
import { useMainstreamCountries } from '@/hooks/queries/use-mainstream-countries';
import { useProfileIdentity } from '@/hooks/queries/use-profile-identity';
import { useProfileNationalClientIdentifier } from '@/hooks/queries/use-profile-national-client-identifier';
import { useProfilePrivacy } from '@/hooks/queries/use-profile-privacy';
import { getNationalClientIdentifierDocumentDefinitions, getPriorityCountryForCitizenships } from '@/utils/national-client-identifier';

const OnboardingVerificationLoading = () => {
  return (
    <VerificationLayout>
      <LoadingCard />
    </VerificationLayout>
  );
};
OnboardingVerificationLoading.displayName = 'OnboardingVerificationLoading';

const OnboardingVerification = ({ prev, sdkConfig, ...props }: VerificationProps) => {
  const { isComplete: isCompleteIdentity, isLoading: isLoadingIdentity, data: identity } = useProfileIdentity();
  const { isLoading: isLoadingPrivacy } = useProfilePrivacy();
  const { isLoading: isLoadingCountries, data: countries } = useMainstreamCountries();
  const { nciPriorityFlowEnabled } = useFlags<LdFeatureFlags>();
  const { isLoading: isLoadingNationalClientIdentifier, data: nationalClientIdentifier } = useProfileNationalClientIdentifier(nciPriorityFlowEnabled);
  const isLoading = isLoadingIdentity || isLoadingPrivacy || isLoadingCountries || isLoadingNationalClientIdentifier;
  const citizenship = identity?.citizenship;
  const citizenship2 = identity?.citizenship2;
  const priorityCitizenship = citizenship ? getPriorityCountryForCitizenships(citizenship, citizenship2, countries ?? {}) : undefined;
  const country = priorityCitizenship ? countries?.[priorityCitizenship]?.alpha3 : undefined;

  if (isLoading) return <OnboardingVerificationLoading />;

  if (!isCompleteIdentity) {
    return <Navigate to="/onboarding/personal-information" replace />;
  }

  if (nciPriorityFlowEnabled && !nationalClientIdentifier) {
    if (prev) return <Navigate to={prev} replace />;

    throw new Error('National client identifier not found with no previous page specified');
  }

  const documentDefinitions = nationalClientIdentifier ? getNationalClientIdentifierDocumentDefinitions(nationalClientIdentifier, countries ?? {}) : undefined;

  return (
    <Verification
      prev={prev}
      sdkConfig={{
        ...sdkConfig,
        country,
        ...(documentDefinitions ? { autoSelectDocumentDefinitions: true, documentDefinitions } : {}),
      }}
      {...props}
    />
  );
};
OnboardingVerification.displayName = 'OnboardingVerification';

export default OnboardingVerification;
